import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";

interface CtaBandProps {
  eyebrow?: string;
  title: ReactNode;
  text?: string;
  primary?: { label: string; to: string };
  secondary?: { label: string; to: string };
  externalPrimary?: { label: string; href: string };
  image?: string;
}

export default function CtaBand({
  eyebrow,
  title,
  text,
  primary,
  secondary,
  externalPrimary,
  image,
}: CtaBandProps) {
  return (
    <section className="relative overflow-hidden bg-foreground-950 text-background-50 grain">
      {image && (
        <>
          <img
            src={image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/85 to-foreground-950/40" />
        </>
      )}

      <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          {eyebrow && (
            <Reveal>
              <p className="eyebrow text-primary-400">{eyebrow}</p>
            </Reveal>
          )}
          <Reveal delay={100}>
            <h2 className="mt-5 font-heading text-3xl leading-tight sm:text-4xl md:text-5xl lg:text-6xl">
              {title}
            </h2>
          </Reveal>
          {text && (
            <Reveal delay={200}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-background-50/70">
                {text}
              </p>
            </Reveal>
          )}
          {(primary || secondary || externalPrimary) && (
            <Reveal delay={300}>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                {primary && (
                  <Link to={primary.to} className="btn-primary">
                    {primary.label}
                    <i className="ri-arrow-right-line text-base" />
                  </Link>
                )}
                {externalPrimary && (
                  <a
                    href={externalPrimary.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    {externalPrimary.label}
                    <i className="ri-arrow-right-up-line text-base" />
                  </a>
                )}
                {secondary && (
                  <Link
                    to={secondary.to}
                    className="btn-ghost text-background-50/80 hover:bg-background-50 hover:text-foreground-950"
                  >
                    {secondary.label}
                  </Link>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}